import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Siren, Upload, CheckCircle2, AlertTriangle, ShieldAlert, ArrowRight, Camera, Gauge, FileCheck, Zap } from 'lucide-react';
import { useLang } from '../i18n';
import { Card, Button, Badge, Input, Spinner, Stepper, Reveal } from '../components/ui';
import SettlementTracker from '../components/SettlementTracker';
import CoverageBanner from '../components/CoverageBanner';
import api, { vnd, fileToBase64 } from '../api';

export default function Claim() {
  const { t, lang } = useLang();
  const locState = useLocation().state;
  const [step, setStep] = useState(0);
  const [policies, setPolicies] = useState([]);
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState('');
  const [injuries, setInjuries] = useState(false);
  const [desc, setDesc] = useState('');
  const [incident, setIncident] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [baseline, setBaseline] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [decision, setDecision] = useState(null);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    api.get('/policies').then((r) => setPolicies(r.data)).catch(() => {});
  }, []);

  const startClaim = async (policyId) => {
    setLoading('start');
    try { const r = await api.post('/claims/start', { policy_id: policyId }); setClaim(r.data); setStep(1); }
    finally { setLoading(''); }
  };

  useEffect(() => {
    if (locState?.policyId && !claim) startClaim(locState.policyId);
    // eslint-disable-next-line
  }, [locState]);

  const submitSafety = async (safe) => {
    setLoading('safety');
    try { await api.post(`/claims/${claim.id}/safety`, { everyone_safe: safe, injuries }); setStep(2); }
    finally { setLoading(''); }
  };

  const submitIntake = async () => {
    setLoading('intake');
    try {
      const r = await api.post(`/claims/${claim.id}/intake`, { description: desc, language: lang });
      setIncident(r.data.incident); setStep(3);
    } finally { setLoading(''); }
  };

  const addEvidence = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setLoading('evidence');
    try {
      const b64 = await fileToBase64(file);
      const r = await api.post(`/claims/${claim.id}/evidence`, { image_base64: b64, label: file.name, is_baseline: baseline });
      const d = r.data.evidence;
      setEvidence((ev) => [...ev, { preview: URL.createObjectURL(file), ...d }]);
    } finally { setLoading(''); e.target.value = ''; }
  };

  const runAssess = async () => {
    setLoading('assess');
    try { const r = await api.post(`/claims/${claim.id}/assess`); setAssessment(r.data); setStep(4); }
    finally { setLoading(''); }
  };

  const runDecision = async () => {
    setLoading('decide');
    try {
      const r = await api.post(`/claims/${claim.id}/decide`); setDecision(r.data);
      const a = await api.get(`/claims/${claim.id}/audit`); setAudit(a.data);
      setStep(5);
    } finally { setLoading(''); }
  };

  const runAutoDemo = async () => {
    setLoading('demo');
    try {
      const r = await api.post('/demo/auto-approve-claim');
      setClaim(r.data.claim);
      setAssessment(r.data.assessment);
      setDecision(r.data.decision);
      const a = await api.get(`/claims/${r.data.claim.id}/audit`); setAudit(a.data);
      setStep(5);
    } finally { setLoading(''); }
  };

  const steps = [t('step_vehicle'), t('c_safety'), t('c_intake'), t('c_evidence'), t('c_assess'), t('c_decision')];

  return (
    <div className="max-w-4xl mx-auto px-5 py-12" data-testid="claim-page">
      <Reveal>
        <div className="kicker">{t('moment_claim')}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-2">{t('claim_h')}</h1>
        <p className="text-slate-400 mt-2">{t('claim_sub')}</p>
      </Reveal>

      <div className="mt-8"><Stepper steps={steps} current={step} /></div>

      {/* STEP 0 — select policy */}
      {step === 0 && (
        <Reveal>
          <div data-testid="claim-select-policy">
            <Card className="p-5 mb-5 border-jade/30 flex items-center justify-between gap-4" data-testid="auto-demo-card">
              <div>
                <div className="flex items-center gap-2 text-jade"><Zap className="w-4 h-4" /><span className="kicker">{t('run_auto_demo')}</span></div>
                <div className="text-sm text-slate-400 mt-1.5">{t('auto_demo_hint')}</div>
              </div>
              <Button data-testid="run-auto-demo" onClick={runAutoDemo} disabled={loading === 'demo'} className="shrink-0">
                {loading === 'demo' ? <Spinner label="…" /> : <><Zap className="w-4 h-4" /> {t('run_auto_demo')}</>}
              </Button>
            </Card>
            <div className="kicker mb-3">{t('pick_policy')}</div>
            {policies.length === 0 && <Card className="p-6 text-slate-400">{t('no_policies')}</Card>}
            <div className="space-y-2">
              {policies.map((p) => (
                <button key={p.id} data-testid={`claim-policy-${p.policy_no}`} onClick={() => startClaim(p.id)} disabled={loading === 'start'}
                  className="card w-full text-left p-4 card-hover flex items-center justify-between">
                  <div><div className="font-mono text-sm">{p.policy_no}</div><div className="text-sm mt-1">{p.vehicle.make} {p.vehicle.model} {p.vehicle.year}</div></div>
                  <ArrowRight className="w-4 h-4 text-jade" />
                </button>
              ))}
            </div>
          </div>
        </Reveal>
      )}

      {/* STEP 1 — safety */}
      {step === 1 && claim && (
        <Reveal>
          <Card className="p-7" data-testid="claim-safety">
            <div className="flex items-center gap-2 mb-5"><ShieldAlert className="w-5 h-5 text-amber" /><h3 className="font-display text-xl font-bold">{t('safety_q')}</h3></div>
            <div className="mb-5">
              <div className="text-sm text-slate-300 mb-2">{t('injuries_q')}</div>
              <div className="flex gap-2">
                <button data-testid="injuries-no" onClick={() => setInjuries(false)} className={`flex-1 py-3 rounded-xl border text-sm ${!injuries ? 'bg-jade/10 text-jade border-jade/40' : 'text-slate-400 border-white/10'}`}>{t('no')}</button>
                <button data-testid="injuries-yes" onClick={() => setInjuries(true)} className={`flex-1 py-3 rounded-xl border text-sm ${injuries ? 'bg-crimson/10 text-crimson border-crimson/40' : 'text-slate-400 border-white/10'}`}>{t('yes')}</button>
              </div>
            </div>
            {injuries && <div className="mb-4 text-crimson text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{t('safe_emergency')}</div>}
            <Button data-testid="safety-continue" onClick={() => submitSafety(!injuries)} disabled={loading === 'safety'} className="w-full">
              {loading === 'safety' ? <Spinner label="…" /> : <>{t('everyone_safe')} — {t('continue')} <ArrowRight className="w-4 h-4" /></>}
            </Button>
          </Card>
        </Reveal>
      )}

      {/* STEP 2 — intake */}
      {step === 2 && (
        <Reveal>
          <Card className="p-6" data-testid="claim-intake">
            <div className="kicker mb-3">{t('describe_q')}</div>
            <textarea data-testid="incident-description" value={desc} onChange={(e) => setDesc(e.target.value)}
              rows={5} placeholder={t('describe_ph')} className="input w-full px-4 py-3 text-sm resize-none" />
            <Button data-testid="intake-analyze" onClick={submitIntake} disabled={loading === 'intake' || !desc} className="w-full mt-4">
              {loading === 'intake' ? <Spinner label={t('analyzing')} /> : <>{t('analyze')} <ArrowRight className="w-4 h-4" /></>}
            </Button>
          </Card>
        </Reveal>
      )}

      {/* STEP 3 — evidence */}
      {step === 3 && (
        <Reveal>
          <div className="space-y-5" data-testid="claim-evidence">
            {incident && (
              <Card className="p-5">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <Badge>{incident.incident_type}</Badge>
                  <Badge tone="slate">{incident.severity_hint}</Badge>
                  {incident.third_party_involved && <Badge tone="amber">third party</Badge>}
                </div>
                <div className="text-xs text-slate-500 mb-1">{t('capture_hint')}</div>
                <ul className="text-sm text-slate-300 list-disc list-inside space-y-1">
                  {incident.suggested_photos.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </Card>
            )}

            <Card className="p-5">
              <label className="flex items-center gap-2 mb-3 text-sm text-slate-300 cursor-pointer">
                <input type="checkbox" data-testid="baseline-toggle" checked={baseline} onChange={(e) => setBaseline(e.target.checked)} /> {t('mark_baseline')}
              </label>
              <label className="card-hover border-2 border-dashed border-white/15 rounded-2xl p-8 flex flex-col items-center justify-center cursor-pointer text-slate-400 hover:text-jade hover:border-jade/40">
                <Camera className="w-7 h-7 mb-2" /><span className="text-sm">{t('add_photo')}</span>
                <input type="file" accept="image/*" className="hidden" data-testid="evidence-upload" onChange={addEvidence} />
              </label>
              {loading === 'evidence' && <div className="mt-3"><Spinner label="…" /></div>}

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
                {evidence.map((ev, i) => (
                  <div key={i} className="card overflow-hidden" data-testid={`evidence-card-${i}`}>
                    <img src={ev.preview} alt="" className="w-full h-24 object-cover" />
                    <div className="p-2">
                      {ev.is_baseline && <Badge tone="slate" className="mb-1">baseline</Badge>}
                      <div className="text-[10px] text-slate-500 font-mono">{t('detected')}:</div>
                      {ev.detection.damages.length ? ev.detection.damages.map((d, j) => (
                        <div key={j} className="text-xs text-slate-300">{d.part} · <span className="text-amber">{d.severity}</span></div>
                      )) : <div className="text-xs text-slate-500">—</div>}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Button data-testid="run-assessment" onClick={runAssess} disabled={loading === 'assess' || evidence.filter(e => !e.is_baseline).length === 0} className="w-full">
              {loading === 'assess' ? <Spinner label={t('assessing')} /> : <>{t('run_assess')} <ArrowRight className="w-4 h-4" /></>}
            </Button>
          </div>
        </Reveal>
      )}

      {/* STEP 4 — assessment */}
      {step === 4 && assessment && (
        <Reveal>
          <div className="space-y-4" data-testid="claim-assessment">
            <CoverageBanner coverage={assessment.coverage_check} />
            <Card className="p-5">
              <div className="kicker mb-3">{t('damage_found')}</div>
              <div className="flex flex-wrap gap-2">
                {assessment.detected_damages.map((d, i) => (
                  <Badge key={i} tone={d.severity === 'severe' ? 'crimson' : d.severity === 'moderate' ? 'amber' : 'slate'}>
                    {d.part} · {d.severity}
                  </Badge>
                ))}
              </div>
              <div className="text-xs text-slate-500 mt-3">{assessment.baseline_comparison.note}</div>
            </Card>

            <div className="grid sm:grid-cols-2 gap-4">
              <Card className="p-5">
                <div className="kicker mb-2">{t('repair_est')}</div>
                {assessment.repair_estimate.line_items.map((li, i) => (
                  <div key={i} className="flex justify-between text-xs py-1 border-b border-white/5">
                    <span className="text-slate-300">{li.part}</span><span className="font-mono">{vnd(li.line_total_vnd)}</span>
                  </div>
                ))}
                <div className="flex justify-between mt-3 pt-2 border-t border-white/10">
                  <span className="font-semibold">{t('total')}</span>
                  <span className="font-mono text-jade" data-testid="repair-total">{vnd(assessment.repair_estimate.total_vnd)}</span>
                </div>
              </Card>

              <div className="space-y-4">
                <Card className="p-5">
                  <div className="kicker mb-2">{t('coverage_check')}</div>
                  <Badge tone={assessment.coverage_check.covered ? 'jade' : 'crimson'}>
                    {assessment.coverage_check.covered ? t('covered') : t('not_covered')}
                  </Badge>
                  <div className="text-xs text-slate-500 mt-2">{assessment.coverage_check.reason}</div>
                </Card>
                <Card className="p-5">
                  <div className="flex items-center justify-between">
                    <div><div className="kicker mb-1">{t('risk_level')}</div><Badge tone={assessment.risk.level === 'HIGH' ? 'crimson' : assessment.risk.level === 'MEDIUM' ? 'amber' : 'jade'}>{assessment.risk.level}</Badge></div>
                    <div className="text-right"><div className="kicker mb-1">{t('ai_conf')}</div><span className="font-mono text-lg text-jade">{Math.round(assessment.ai_confidence * 100)}%</span></div>
                  </div>
                  <ul className="text-xs text-slate-400 mt-3 space-y-1">
                    {assessment.risk.signals.map((s, i) => <li key={i} className="flex items-center gap-1"><Gauge className="w-3 h-3 text-slate-500" />{s}</li>)}
                  </ul>
                </Card>
              </div>
            </div>

            <Button data-testid="get-decision" onClick={runDecision} disabled={loading === 'decide'} className="w-full">
              {loading === 'decide' ? <Spinner label={t('deciding')} /> : <>{t('get_decision')} <ArrowRight className="w-4 h-4" /></>}
            </Button>
          </div>
        </Reveal>
      )}

      {/* STEP 5 — decision */}
      {step === 5 && decision && (
        <Reveal>
          <div className="space-y-4" data-testid="claim-decision">
            {assessment && <CoverageBanner coverage={assessment.coverage_check} />}
            <Card className={`p-7 relative overflow-hidden ${decision.decision === 'approve' ? 'border-jade/40' : decision.decision === 'escalate' ? 'border-amber/40' : 'border-white/10'}`}>
              <div className="flex items-center gap-3">
                {decision.decision === 'approve' ? <CheckCircle2 className="w-8 h-8 text-jade" /> : decision.decision === 'escalate' ? <ShieldAlert className="w-8 h-8 text-amber" /> : <AlertTriangle className="w-8 h-8 text-slate-300" />}
                <div>
                  <div className="font-display text-2xl font-extrabold" data-testid="decision-result">
                    {decision.decision === 'approve' ? t('decision_approve') : decision.decision === 'escalate' ? t('decision_escalate') : t('decision_request')}
                  </div>
                  <div className="text-sm text-slate-400">{t('ai_conf')}: {Math.round(decision.confidence * 100)}%</div>
                </div>
              </div>

              <div className="mt-5">
                <div className="kicker mb-2">{t('rules_applied')}</div>
                <ul className="space-y-1.5">
                  {decision.rules_triggered.map((r, i) => (
                    <li key={i} className="text-sm text-slate-300 flex items-start gap-2"><FileCheck className="w-4 h-4 text-jade shrink-0 mt-0.5" />{r}</li>
                  ))}
                </ul>
              </div>

              {decision.decision === 'escalate' && <div className="mt-4 text-sm text-amber">{t('escalate_note')}</div>}
              {decision.decision === 'request_more' && <div className="mt-4 text-sm text-slate-400">{t('request_note')}</div>}

              {decision.settlement && (
                <div className="mt-5 card p-4 border-jade/20" data-testid="settlement-card">
                  <div className="kicker mb-2">{t('settlement')}</div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><div className="text-xs text-slate-500">{t('amount')}</div><span className="font-mono text-jade text-lg">{vnd(decision.settlement.amount_vnd)}</span></div>
                    <div><div className="text-xs text-slate-500">{t('eta')}</div>{decision.settlement.eta_days} {t('days')}</div>
                    <div className="col-span-2"><div className="text-xs text-slate-500">{t('garage')}</div>{decision.settlement.garage}</div>
                  </div>
                </div>
              )}
            </Card>

            {decision.decision === 'approve' && claim && <SettlementTracker claimId={claim.id} />}

            <Card className="p-5" data-testid="audit-trail">
              <div className="kicker mb-3">{t('audit_trail')}</div>
              <div className="space-y-2">
                {audit.map((a) => (
                  <div key={a.id} className="flex items-start gap-3 text-xs">
                    <span className="font-mono text-slate-500 w-20 shrink-0">{a.timestamp.slice(11, 19)}</span>
                    <span className="text-jade font-mono">{a.event}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </Reveal>
      )}
    </div>
  );
}
